/*
 * BuildDocs пользовательский интерфейс version 0.1.0
 *
 * Copyright © 2021 ООО АДАПТ info@acfs.spb.ru
 * You may use, distribute and modify this code under the
 * terms of the ООО АДАПТ license
 */

import React, { useEffect, useMemo, useState } from 'react'

import { UUID, StageResponse } from '@builddocs/data-management'
import { useDisclosure } from '@builddocs/system'
import {
  Dialog,
  Typography,
  Button,
  Stack,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  DialogContentText,
} from '@mui/material'
import Draggable from 'react-draggable'

import TableForSelect from './TableForSelect'

type Props = {
  title: string
  allWorks: Record<UUID, StageResponse>
  initialWorks: UUID[]
  onSelect: (sectionId: UUID[]) => void
}

function PaperComponent(props) {
  return (
    <Draggable
      cancel={'[class*="MuiDialogContent-root"]'}
      handle="#draggable-dialog-title"
    >
      <Paper {...props} />
    </Draggable>
  )
}

/**
 *Диалог выбора работ
 */

function WorksDialog(props: Props) {
  const { title, onSelect, allWorks, initialWorks, ...rest } = props

  const { isOpen, onOpen, onClose } = useDisclosure()

  const [selected, setSelected] = useState<UUID[]>(initialWorks)

  useEffect(() => setSelected(initialWorks), [initialWorks]) // обновления состояния в случае изменения первоначальнонго списка орг-й

  const hasValue = useMemo(() => selected.length > 0, [selected])

  const handleSelect = (sectionId: UUID | UUID[]) => {
    if (Array.isArray(sectionId)) {
      setSelected(sectionId)
    } else {
      setSelected(prev =>
        prev.includes(sectionId)
          ? prev.filter(_sectionId => _sectionId !== sectionId)
          : [...prev, sectionId],
      )
    }
  }

  const handleClose = () => {
    setSelected(initialWorks)
    onClose()
  }

  const handleSave = () => {
    hasValue ? onSelect && onSelect(selected) : onSelect && onSelect([])
    onClose()
  }

  const handleOpen = (event: React.MouseEvent) => {
    event.preventDefault()
    onOpen()
  }

  return (
    <>
      <Stack alignItems="flex-start" {...rest}>
        {initialWorks?.map(sectionId => (
          <Typography
            key={sectionId}
            color="text.secondary"
            variant="subtitle2"
          >
            {`${allWorks[sectionId].title}, место проведения работ: ${allWorks[sectionId].place}`}
          </Typography>
        ))}
        <Button onClick={handleOpen}>{title}</Button>
      </Stack>
      <Dialog
        PaperComponent={PaperComponent}
        aria-labelledby="draggable-dialog-title"
        id="material-dialog-modal"
        maxWidth="md"
        open={isOpen}
        onClose={handleClose}
      >
        <DialogTitle id="draggable-dialog-title" style={{ cursor: 'move' }}>
          {title}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Список работ, на основании которых сформировано закрытие
          </DialogContentText>
          <TableForSelect
            items={allWorks}
            selected={selected}
            tableType="works"
            onSelect={handleSelect}
          />
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={handleClose}>
            Отмена
          </Button>
          <Button variant="contained" onClick={handleSave}>
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default WorksDialog
